import { useEffect, useState } from "react";
import { api, formatINR, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Star, Package as PackageIcon, X, Image as ImageIcon, ListChecks, Search as SearchIcon, MapPin, CreditCard, Globe, Clock, CalendarDays, TrendingUp, Users } from "lucide-react";
import { SEOEditor } from "@/components/SERPPreview";
import { useNavigate } from "react-router-dom";

const EMPTY = {
  category: "tour", sub_category: "", title: "", slug: "", short_desc: "", description: "",
  destination: "", duration: "", min_pax: 1, max_pax: 50,
  adult_price: 0, child_price: 0, infant_price: 0, senior_price: 0,
  // Vehicle pricing (cabs, FIT tours)
  vehicle_options: [], // [{type, seats, price, description}]
  // Room pricing (hotels, staycations)
  room_categories: [], // [{name, max_adults, max_children, price_per_night, extra_adult_charge, extra_child_charge}]
  check_in_time: "", check_out_time: "",
  booking_type: "", // per_person, per_vehicle, per_room — auto-set from category
  gst_rate: 5, service_charge: 2,
  // Per-product age labels
  adult_age_label: "", child_age_label: "", infant_age_label: "", senior_age_label: "",
  // Weekday / Weekend pricing
  weekend_adult_price: null, weekend_child_price: null, weekend_infant_price: null,
  // Availability
  available_days: [0, 1, 2, 3, 4, 5, 6], // 0=Sun, 1=Mon ... 6=Sat
  blackout_dates: [],
  // Per-product cancellation
  cancellation_rules: [], // [{hours_before: 168, refund_pct: 100, label: "7+ days: Full refund"}, ...]
  cancellation_policy: "", // Free-text description
  allow_partial_cancellation: false,
  allow_modification: false,
  // Loyalty
  loyalty_enabled: false,
  loyalty_points_rate: null, // null = use global rate
  includes: [], excludes: [], itinerary: [],
  amenities: [], images: [], featured_image: "",
  is_featured: false, venue: "", starts_from: "", tags: [],
  deal_active: false, deal_original_price: 0, deal_expires_at: "", deal_label: "",
  seo_title: "", seo_description: "", og_image: "", focus_keyword: "",
  advance_payment_enabled: false, advance_payment_percent: 50, advance_payment_fixed: 0,
  seats_total: 0, booking_cutoff_hours: 0,
  online_booking_enabled: undefined,
};

function slugify(s) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function PickupPointsSection({ productId }) {
  const [points, setPoints] = useState([]);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: "", address: "", city: "Hyderabad", time: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/pickup-points", { params: { product_id: productId } })
      .then((r) => setPoints(r.data));
  }, [productId]);

  const addPoint = async () => {
    if (!draft.name.trim()) return;
    setSaving(true);
    try {
      const res = await api.post("/admin/pickup-points", { ...draft, product_id: productId, display_order: points.length });
      setPoints([...points, res.data]);
      setDraft({ name: "", address: "", city: "Hyderabad", time: "" });
      setAdding(false);
      toast.success("Pickup point added");
    } catch { toast.error("Failed to add"); }
    finally { setSaving(false); }
  };

  const removePoint = async (id) => {
    await api.delete(`/admin/pickup-points/${id}`);
    setPoints(points.filter((p) => p.id !== id));
    toast.success("Removed");
  };

  return (
    <div className="border border-border rounded-xl p-4 space-y-3" data-testid="pickup-points-section">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold flex items-center gap-2 text-sm"><MapPin className="w-4 h-4 text-primary" /> Pickup Points</h3>
        <Button variant="outline" size="sm" onClick={() => setAdding(!adding)} data-testid="add-pickup-btn">
          <Plus className="w-3 h-3 mr-1" /> Add Point
        </Button>
      </div>
      {points.length > 0 && (
        <div className="space-y-1">
          {points.map((p) => (
            <div key={p.id} className="flex items-center justify-between bg-muted/50 rounded-lg px-3 py-2 text-sm" data-testid={`pickup-${p.id}`}>
              <div>
                <span className="font-medium">{p.name}</span>
                {p.time && <span className="text-muted-foreground ml-2">({p.time})</span>}
                <span className="text-muted-foreground ml-2">— {p.address}</span>
              </div>
              <button onClick={() => removePoint(p.id)} className="text-destructive hover:opacity-70"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
      {adding && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 bg-secondary/30 p-3 rounded-lg">
          <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Name" data-testid="pickup-name-input" />
          <Input value={draft.address} onChange={(e) => setDraft({ ...draft, address: e.target.value })} placeholder="Address" />
          <Input value={draft.time} onChange={(e) => setDraft({ ...draft, time: e.target.value })} placeholder="06:30 AM" />
          <Button onClick={addPoint} disabled={saving} size="sm" data-testid="pickup-save-btn">{saving ? "..." : "Add"}</Button>
        </div>
      )}
      {points.length === 0 && !adding && (
        <p className="text-xs text-muted-foreground">No pickup points yet. Add stops where guests are picked up.</p>
      )}
    </div>
  );
}

function ArrayInput({ label, values, setValues, placeholder, testid }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    if (!draft.trim()) return;
    setValues([...(values || []), draft.trim()]);
    setDraft("");
  };
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex gap-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={placeholder} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} data-testid={testid} />
        <Button type="button" variant="outline" onClick={add} size="sm"><Plus className="w-4 h-4" /></Button>
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        {(values || []).map((v, i) => (
          <Badge key={i} variant="secondary" className="gap-1 pr-1">
            {v}
            <button type="button" onClick={() => setValues(values.filter((_, j) => j !== i))} className="hover:text-destructive ml-1"><X className="w-3 h-3" /></button>
          </Badge>
        ))}
      </div>
    </div>
  );
}

export function ProductForm({ initial, onSubmit, onCancel, categories: CATEGORIES = [], subs: SUBS = {}, dbCategories = [] }) {
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const [saving, setSaving] = useState(false);
  const [subTypes, setSubTypes] = useState([]);
  const isEdit = !!initial?.id;

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  // Build a lookup: product_category → booking_type from DB categories
  const catBookingTypeMap = {};
  dbCategories.forEach((c) => {
    const pc = c.product_category || c.slug;
    if (c.booking_type) catBookingTypeMap[pc] = c.booking_type;
  });

  // Resolve booking type from category
  const resolveBookingType = (cat) => {
    return catBookingTypeMap[cat] || { hotel: "per_room", staycation: "per_room", cab: "per_vehicle", coach: "per_vehicle" }[cat] || "per_person";
  };
  const currentBt = resolveBookingType(form.category);

  // Load sub-types dynamically when category changes
  useEffect(() => {
    if (form.category) {
      api.get(`/categories/sub-types/${form.category}`).then((r) => {
        setSubTypes(r.data || []);
      }).catch(() => setSubTypes([]));
    } else {
      setSubTypes([]);
    }
  }, [form.category]);

  const addItineraryDay = () => {
    set("itinerary", [...(form.itinerary || []), { day: (form.itinerary?.length || 0) + 1, title: "", desc: "" }]);
  };
  const updateDay = (i, k, v) => {
    const next = [...form.itinerary];
    next[i] = { ...next[i], [k]: k === "day" ? Number(v) : v };
    set("itinerary", next);
  };
  const removeDay = (i) => set("itinerary", form.itinerary.filter((_, j) => j !== i));

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.slug.trim()) return toast.error("Slug is required");
    const bt = currentBt;
    if (bt === "per_person" && !form.adult_price) return toast.error("Adult price is required");
    if (bt === "per_vehicle" && (!form.vehicle_options || form.vehicle_options.length === 0) && !form.adult_price) return toast.error("Add at least one vehicle option or set a fallback price");
    if (bt === "per_room" && (!form.room_categories || form.room_categories.length === 0)) return toast.error("Add at least one room category");
    if (!form.featured_image && (!form.images || !form.images.length)) {
      return toast.error("At least 1 image is required");
    }
    setSaving(true);
    try {
      await onSubmit({
        ...form,
        booking_type: bt,
        adult_price: Number(form.adult_price) || 0,
        child_price: Number(form.child_price) || 0,
        infant_price: Number(form.infant_price) || 0,
        senior_price: Number(form.senior_price) || 0,
        gst_rate: Number(form.gst_rate) || 0,
        service_charge: Number(form.service_charge) || 0,
        min_pax: Number(form.min_pax) || 1,
        max_pax: Number(form.max_pax) || 50,
        featured_image: form.featured_image || form.images?.[0] || "",
        vehicle_options: form.vehicle_options || [],
        room_categories: form.room_categories || [],
        check_in_time: form.check_in_time || "",
        check_out_time: form.check_out_time || "",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 py-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Category *</Label>
          <Select value={form.category} onValueChange={(v) => { set("category", v); set("sub_category", ""); }}>
            <SelectTrigger data-testid="prod-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              {/* Deduplicate by product_category to avoid duplicate values in Select */}
              {(() => {
                const seen = new Set();
                return dbCategories.filter((c) => !c.parent_id).filter((c) => {
                  const pc = c.product_category || c.slug;
                  if (seen.has(pc)) return false;
                  seen.add(pc);
                  return true;
                }).map((c) => {
                  const pc = c.product_category || c.slug;
                  return <SelectItem key={pc} value={pc} className="capitalize">{c.name}</SelectItem>;
                });
              })()}
              {dbCategories.length === 0 && <SelectItem value="tour">Tour</SelectItem>}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Sub-category</Label>
          <Select value={form.sub_category || "_none"} onValueChange={(v) => set("sub_category", v === "_none" ? "" : v)}>
            <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">-- None --</SelectItem>
              {subTypes.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
              {/* Also show children categories from tree as sub-category options */}
              {dbCategories.filter((c) => {
                const parentCat = dbCategories.find((p) => !p.parent_id && (p.product_category || p.slug) === form.category);
                return parentCat && c.parent_id === parentCat.id;
              }).map((c) => (
                !subTypes.includes(c.name) && <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label>Title *</Label>
          <Input value={form.title} onChange={(e) => set("title", e.target.value)} onBlur={() => !form.slug && set("slug", slugify(form.title))} data-testid="prod-title" />
        </div>
        <div>
          <Label>Slug * (URL identifier)</Label>
          <Input value={form.slug} onChange={(e) => set("slug", slugify(e.target.value))} data-testid="prod-slug" />
        </div>
        <div>
          <Label>Destination</Label>
          <Input value={form.destination || ""} onChange={(e) => set("destination", e.target.value)} placeholder="Hyderabad" />
        </div>
        <div>
          <Label>Duration</Label>
          <Input value={form.duration || ""} onChange={(e) => set("duration", e.target.value)} placeholder="1 Day" />
        </div>
        <div>
          <Label>Venue (for tickets)</Label>
          <Input value={form.venue || ""} onChange={(e) => set("venue", e.target.value)} />
        </div>
        <div>
          <Label>Venue Latitude</Label>
          <Input type="number" step="any" value={form.venue_lat || ""} onChange={(e) => set("venue_lat", e.target.value ? Number(e.target.value) : null)} placeholder="17.3616" />
        </div>
        <div>
          <Label>Venue Longitude</Label>
          <Input type="number" step="any" value={form.venue_lng || ""} onChange={(e) => set("venue_lng", e.target.value ? Number(e.target.value) : null)} placeholder="78.4747" />
        </div>
        <div className="sm:col-span-2">
          <Label>Short description *</Label>
          <Textarea rows={2} value={form.short_desc} onChange={(e) => set("short_desc", e.target.value)} data-testid="prod-short-desc" />
        </div>
        <div className="sm:col-span-2">
          <Label>Full description *</Label>
          <Textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="prod-description" />
        </div>
      </div>

      <div className="grid sm:grid-cols-4 gap-4">
        {/* Category-specific pricing fields */}
        {(() => {
          const bt = currentBt;

          if (bt === "per_vehicle") return (
            <>
              <div className="sm:col-span-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-sm text-blue-700 dark:text-blue-300 mb-3">
                  <strong>Vehicle-Based Pricing:</strong> Add vehicle types with seat capacity and price per vehicle.
                </div>
                <div className="flex justify-between items-center mb-2">
                  <Label>Vehicle Options</Label>
                  <Button type="button" variant="outline" size="sm" onClick={() => set("vehicle_options", [...(form.vehicle_options || []), { type: "", seats: 4, price: 0, description: "" }])} data-testid="add-vehicle-option">
                    <Plus className="w-4 h-4 mr-1" />Add Vehicle
                  </Button>
                </div>
                <div className="space-y-2">
                  {(form.vehicle_options || []).map((v, i) => (
                    <div key={i} className="grid grid-cols-[1fr_80px_120px_1fr_auto] gap-2 items-end" data-testid={`vehicle-opt-${i}`}>
                      <div><Label className="text-xs">Type</Label><Input value={v.type} onChange={(e) => { const vo = [...form.vehicle_options]; vo[i] = { ...vo[i], type: e.target.value }; set("vehicle_options", vo); }} placeholder="sedan" /></div>
                      <div><Label className="text-xs">Seats</Label><Input type="number" value={v.seats} onChange={(e) => { const vo = [...form.vehicle_options]; vo[i] = { ...vo[i], seats: Number(e.target.value) }; set("vehicle_options", vo); }} /></div>
                      <div><Label className="text-xs">Price ₹</Label><Input type="number" value={v.price} onChange={(e) => { const vo = [...form.vehicle_options]; vo[i] = { ...vo[i], price: Number(e.target.value) }; set("vehicle_options", vo); }} /></div>
                      <div><Label className="text-xs">Description</Label><Input value={v.description || ""} onChange={(e) => { const vo = [...form.vehicle_options]; vo[i] = { ...vo[i], description: e.target.value }; set("vehicle_options", vo); }} placeholder="AC, Innova Crysta" /></div>
                      <Button variant="ghost" size="icon" onClick={() => set("vehicle_options", form.vehicle_options.filter((_, j) => j !== i))} className="text-destructive h-9 w-9"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  ))}
                </div>
              </div>
              <div><Label>Fallback Price ₹</Label><Input type="number" value={form.adult_price} onChange={(e) => set("adult_price", e.target.value)} data-testid="prod-adult-price" /></div>
              <div><Label>GST %</Label><Input type="number" value={form.gst_rate} onChange={(e) => set("gst_rate", e.target.value)} /></div>
              <div><Label>Service %</Label><Input type="number" value={form.service_charge} onChange={(e) => set("service_charge", e.target.value)} /></div>
            </>
          );

          if (bt === "per_room") return (
            <>
              <div className="sm:col-span-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-sm text-blue-700 dark:text-blue-300 mb-3">
                  <strong>Room-Based Pricing:</strong> Add room categories with capacity and nightly rates.
                </div>
                <div className="flex justify-between items-center mb-2">
                  <Label>Room Categories</Label>
                  <Button type="button" variant="outline" size="sm" onClick={() => set("room_categories", [...(form.room_categories || []), { name: "", max_adults: 2, max_children: 2, price_per_night: 0, extra_adult_charge: 0, extra_child_charge: 0 }])} data-testid="add-room-category">
                    <Plus className="w-4 h-4 mr-1" />Add Room
                  </Button>
                </div>
                <div className="space-y-3">
                  {(form.room_categories || []).map((r, i) => (
                    <div key={i} className="border border-border rounded-lg p-3 space-y-2 relative" data-testid={`room-cat-${i}`}>
                      <button onClick={() => set("room_categories", form.room_categories.filter((_, j) => j !== i))} type="button" className="absolute top-2 right-2 text-muted-foreground hover:text-destructive"><X className="w-4 h-4" /></button>
                      <div className="grid grid-cols-[1fr_80px_80px_120px] gap-2">
                        <div><Label className="text-xs">Room Name</Label><Input value={r.name} onChange={(e) => { const rc = [...form.room_categories]; rc[i] = { ...rc[i], name: e.target.value }; set("room_categories", rc); }} placeholder="Deluxe Room" /></div>
                        <div><Label className="text-xs">Max Adults</Label><Input type="number" value={r.max_adults} onChange={(e) => { const rc = [...form.room_categories]; rc[i] = { ...rc[i], max_adults: Number(e.target.value) }; set("room_categories", rc); }} /></div>
                        <div><Label className="text-xs">Max Children</Label><Input type="number" value={r.max_children} onChange={(e) => { const rc = [...form.room_categories]; rc[i] = { ...rc[i], max_children: Number(e.target.value) }; set("room_categories", rc); }} /></div>
                        <div><Label className="text-xs">₹ / Night</Label><Input type="number" value={r.price_per_night} onChange={(e) => { const rc = [...form.room_categories]; rc[i] = { ...rc[i], price_per_night: Number(e.target.value) }; set("room_categories", rc); }} /></div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div><Label className="text-xs">Extra Adult ₹/night</Label><Input type="number" value={r.extra_adult_charge || 0} onChange={(e) => { const rc = [...form.room_categories]; rc[i] = { ...rc[i], extra_adult_charge: Number(e.target.value) }; set("room_categories", rc); }} /></div>
                        <div><Label className="text-xs">Extra Child ₹/night</Label><Input type="number" value={r.extra_child_charge || 0} onChange={(e) => { const rc = [...form.room_categories]; rc[i] = { ...rc[i], extra_child_charge: Number(e.target.value) }; set("room_categories", rc); }} /></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div><Label>Check-in Time</Label><Input value={form.check_in_time || ""} onChange={(e) => set("check_in_time", e.target.value)} placeholder="2:00 PM" /></div>
              <div><Label>Check-out Time</Label><Input value={form.check_out_time || ""} onChange={(e) => set("check_out_time", e.target.value)} placeholder="11:00 AM" /></div>
              <div><Label>Fallback Price ₹</Label><Input type="number" value={form.adult_price} onChange={(e) => set("adult_price", e.target.value)} data-testid="prod-adult-price" /></div>
              <div><Label>GST %</Label><Input type="number" value={form.gst_rate} onChange={(e) => set("gst_rate", e.target.value)} /></div>
              <div><Label>Service %</Label><Input type="number" value={form.service_charge} onChange={(e) => set("service_charge", e.target.value)} /></div>
            </>
          );

          // Default per_person
          return (
            <>
              <div className="sm:col-span-4"><h4 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Weekday Pricing (Mon-Fri)</h4></div>
              <div><Label>Adult ₹ *</Label><Input type="number" value={form.adult_price} onChange={(e) => set("adult_price", e.target.value)} data-testid="prod-adult-price" /></div>
              <div><Label>Child ₹</Label><Input type="number" value={form.child_price} onChange={(e) => set("child_price", e.target.value)} /></div>
              <div><Label>Infant ₹</Label><Input type="number" value={form.infant_price} onChange={(e) => set("infant_price", e.target.value)} /></div>
              <div><Label>Senior ₹</Label><Input type="number" value={form.senior_price} onChange={(e) => set("senior_price", e.target.value)} /></div>
              <div className="sm:col-span-4 border-t pt-3 mt-1"><h4 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Weekend Pricing (Sat-Sun) <span className="text-[10px] font-normal">(leave blank to use weekday prices)</span></h4></div>
              <div><Label>Adult ₹</Label><Input type="number" value={form.weekend_adult_price ?? ""} onChange={(e) => set("weekend_adult_price", e.target.value ? Number(e.target.value) : null)} placeholder="Same as weekday" /></div>
              <div><Label>Child ₹</Label><Input type="number" value={form.weekend_child_price ?? ""} onChange={(e) => set("weekend_child_price", e.target.value ? Number(e.target.value) : null)} placeholder="Same" /></div>
              <div><Label>Infant ₹</Label><Input type="number" value={form.weekend_infant_price ?? ""} onChange={(e) => set("weekend_infant_price", e.target.value ? Number(e.target.value) : null)} placeholder="Same" /></div>
              <div></div>
              <div className="sm:col-span-4 border-t pt-3 mt-1"><h4 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Rates & Tax</h4></div>
              <div><Label>GST %</Label><Input type="number" value={form.gst_rate} onChange={(e) => set("gst_rate", e.target.value)} /></div>
              <div><Label>Service %</Label><Input type="number" value={form.service_charge} onChange={(e) => set("service_charge", e.target.value)} /></div>
              <div><Label>Min pax</Label><Input type="number" value={form.min_pax} onChange={(e) => set("min_pax", e.target.value)} /></div>
              <div><Label>Max pax</Label><Input type="number" value={form.max_pax} onChange={(e) => set("max_pax", e.target.value)} /></div>
            </>
          );
        })()}
      </div>

      <ArrayInput label="Image URLs *" values={form.images} setValues={(v) => set("images", v)} placeholder="https://images.unsplash.com/..." testid="prod-image-input" />
      <div>
        <Label>Featured image URL (leave blank to use first image)</Label>
        <Input value={form.featured_image || ""} onChange={(e) => set("featured_image", e.target.value)} placeholder="https://..." />
      </div>

      {/* Per-product Age Labels */}
      <div className="border border-border rounded-xl p-4 space-y-3">
        <h3 className="font-semibold flex items-center gap-2 text-sm"><Users className="w-4 h-4 text-primary" /> Age Labels (per product)</h3>
        <p className="text-xs text-muted-foreground">Leave blank to use global defaults from Admin Settings.</p>
        <div className="grid sm:grid-cols-4 gap-3">
          <div><Label className="text-xs">Adult label</Label><Input value={form.adult_age_label || ""} onChange={(e) => set("adult_age_label", e.target.value)} placeholder="12+ years" /></div>
          <div><Label className="text-xs">Child label</Label><Input value={form.child_age_label || ""} onChange={(e) => set("child_age_label", e.target.value)} placeholder="5-11 years" /></div>
          <div><Label className="text-xs">Infant label</Label><Input value={form.infant_age_label || ""} onChange={(e) => set("infant_age_label", e.target.value)} placeholder="0-4 years" /></div>
          <div><Label className="text-xs">Senior label</Label><Input value={form.senior_age_label || ""} onChange={(e) => set("senior_age_label", e.target.value)} placeholder="60+ years" /></div>
        </div>
      </div>

      {/* Available Days */}
      <div className="border border-border rounded-xl p-4 space-y-3">
        <h3 className="font-semibold flex items-center gap-2 text-sm"><CalendarDays className="w-4 h-4 text-primary" /> Available Days & Blackout Dates</h3>
        <p className="text-xs text-muted-foreground">Uncheck days when this product is not available. Add blackout dates for specific dates.</p>
        <div className="flex flex-wrap gap-2">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d, i) => {
            const days = form.available_days || [0,1,2,3,4,5,6];
            const checked = days.includes(i);
            return (
              <label key={d} className={`flex items-center gap-1.5 cursor-pointer border rounded-lg px-3 py-2 text-sm transition-colors ${checked ? "bg-primary/10 border-primary text-primary font-medium" : "bg-secondary/30 border-border text-muted-foreground"}`}>
                <input type="checkbox" checked={checked} onChange={(e) => {
                  const next = e.target.checked ? [...days, i].sort() : days.filter((x) => x !== i);
                  set("available_days", next);
                }} className="w-3.5 h-3.5 rounded" />
                {d}
              </label>
            );
          })}
        </div>
        <div className="space-y-2">
          <Label className="text-xs">Blackout Dates (comma-separated, e.g. 2026-01-26, 2026-08-15)</Label>
          <Input
            value={(form.blackout_dates || []).join(", ")}
            onChange={(e) => set("blackout_dates", e.target.value.split(",").map((d) => d.trim()).filter(Boolean))}
            placeholder="2026-01-26, 2026-08-15"
            data-testid="prod-blackout-dates"
          />
        </div>
      </div>

      <ArrayInput label="Includes" values={form.includes} setValues={(v) => set("includes", v)} placeholder="AC coach, lunch..." />
      <ArrayInput label="Excludes" values={form.excludes} setValues={(v) => set("excludes", v)} placeholder="Personal expenses..." />
      <ArrayInput label="Amenities (for hotels)" values={form.amenities} setValues={(v) => set("amenities", v)} placeholder="Pool, Spa..." />
      <ArrayInput label="Tags" values={form.tags} setValues={(v) => set("tags", v)} placeholder="family, popular..." />

      <div>
        <div className="flex justify-between items-center mb-2">
          <Label>Itinerary (day-wise)</Label>
          <Button type="button" variant="outline" size="sm" onClick={addItineraryDay} data-testid="add-day"><Plus className="w-4 h-4 mr-1" />Add day</Button>
        </div>
        <div className="space-y-3">
          {(form.itinerary || []).map((d, i) => (
            <div key={i} className="border border-border rounded-lg p-3 space-y-2 relative">
              <button onClick={() => removeDay(i)} type="button" className="absolute top-2 right-2 text-muted-foreground hover:text-destructive"><X className="w-4 h-4" /></button>
              <div className="grid sm:grid-cols-[80px_1fr] gap-2">
                <Input type="number" min={1} value={d.day} onChange={(e) => updateDay(i, "day", e.target.value)} placeholder="Day" />
                <Input value={d.title} onChange={(e) => updateDay(i, "title", e.target.value)} placeholder="Day title" />
              </div>
              <Textarea rows={2} value={d.desc} onChange={(e) => updateDay(i, "desc", e.target.value)} placeholder="Activities of the day..." />
            </div>
          ))}
        </div>
      </div>

      <div className="border border-border rounded-xl p-4 space-y-4">
        <h3 className="font-semibold flex items-center gap-2 text-sm"><X className="w-4 h-4 text-destructive" /> Cancellation & Modification</h3>

        {/* Cancellation Rules Tiers */}
        <div>
          <Label className="mb-2 block">Cancellation Rules (tiers)</Label>
          <p className="text-xs text-muted-foreground mb-2">Define refund tiers. Each tier: if cancelled X+ hours before travel → Y% refund. Evaluated top-down, first match wins.</p>
          <div className="space-y-2">
            {(form.cancellation_rules || []).map((rule, i) => (
              <div key={i} className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1">
                  <Input type="number" min={0} className="w-20" placeholder="Hours" value={rule.hours_before ?? ""} onChange={(e) => {
                    const rules = [...(form.cancellation_rules || [])]; rules[i] = { ...rules[i], hours_before: Number(e.target.value) || 0 }; set("cancellation_rules", rules);
                  }} data-testid={`cancel-rule-hours-${i}`} />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">hrs before →</span>
                </div>
                <div className="flex items-center gap-1">
                  <Input type="number" min={0} max={100} className="w-20" placeholder="%" value={rule.refund_pct ?? ""} onChange={(e) => {
                    const rules = [...(form.cancellation_rules || [])]; rules[i] = { ...rules[i], refund_pct: Number(e.target.value) || 0 }; set("cancellation_rules", rules);
                  }} data-testid={`cancel-rule-pct-${i}`} />
                  <span className="text-xs text-muted-foreground">% refund</span>
                </div>
                <Input className="flex-1 min-w-[140px]" placeholder="Label (e.g. 7+ days: Full refund)" value={rule.label || ""} onChange={(e) => {
                  const rules = [...(form.cancellation_rules || [])]; rules[i] = { ...rules[i], label: e.target.value }; set("cancellation_rules", rules);
                }} data-testid={`cancel-rule-label-${i}`} />
                <Button type="button" variant="ghost" size="sm" className="text-destructive" onClick={() => {
                  const rules = [...(form.cancellation_rules || [])]; rules.splice(i, 1); set("cancellation_rules", rules);
                }}><X className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
          <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => {
            set("cancellation_rules", [...(form.cancellation_rules || []), { hours_before: 0, refund_pct: 0, label: "" }]);
          }} data-testid="add-cancel-rule"><Plus className="w-4 h-4 mr-1" />Add Tier</Button>
        </div>

        {/* Cancellation Policy Text */}
        <div>
          <Label>Cancellation Policy Text (shown to customers)</Label>
          <Textarea rows={2} value={form.cancellation_policy || ""} onChange={(e) => set("cancellation_policy", e.target.value)} placeholder="e.g. Free cancellation up to 48 hours before travel..." data-testid="cancellation-policy-text" />
        </div>

        {/* Partial cancellation toggle */}
        <div className="flex items-center gap-3">
          <Switch checked={!!form.allow_partial_cancellation} onCheckedChange={(v) => set("allow_partial_cancellation", v)} id="allow-partial" data-testid="allow-partial-cancel" />
          <Label htmlFor="allow-partial" className="cursor-pointer">Allow partial cancellation (reduce guests)</Label>
        </div>

        {/* Modification toggle */}
        <div className="flex items-center gap-3">
          <Switch checked={!!form.allow_modification} onCheckedChange={(v) => set("allow_modification", v)} id="allow-mod" data-testid="allow-modification" />
          <Label htmlFor="allow-mod" className="cursor-pointer">Allow date modification</Label>
        </div>
      </div>

      {/* Loyalty Points */}
      <div className="border border-border rounded-xl p-4 space-y-3">
        <h3 className="font-semibold flex items-center gap-2 text-sm"><Star className="w-4 h-4 text-saffron" /> Loyalty Points</h3>
        <div className="flex items-center gap-3">
          <Switch checked={!!form.loyalty_enabled} onCheckedChange={(v) => set("loyalty_enabled", v)} id="loyalty-on" data-testid="prod-loyalty-enabled" />
          <Label htmlFor="loyalty-on" className="cursor-pointer">Enable loyalty points for this product</Label>
        </div>
        {form.loyalty_enabled && (
          <div>
            <Label>Custom earn rate (points per ₹100) <span className="text-xs text-muted-foreground">Leave empty to use global rate</span></Label>
            <Input type="number" min={0} value={form.loyalty_points_rate ?? ""} onChange={(e) => set("loyalty_points_rate", e.target.value === "" ? null : Number(e.target.value))} placeholder="Global default" className="max-w-[200px]" data-testid="prod-loyalty-rate" />
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        <Switch checked={!!form.is_featured} onCheckedChange={(v) => set("is_featured", v)} id="featured" data-testid="prod-featured" />
        <Label htmlFor="featured" className="cursor-pointer">Show on Featured carousel</Label>
      </div>

      <div className="border border-border rounded-xl p-4 bg-secondary/30 space-y-4">
        <div className="flex items-center gap-3">
          <Switch checked={!!form.deal_active} onCheckedChange={(v) => set("deal_active", v)} id="deal_active" data-testid="prod-deal-active" />
          <Label htmlFor="deal_active" className="cursor-pointer font-semibold">🔥 Flash-sale / Deal enabled</Label>
        </div>
        {form.deal_active && (
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label>Original ₹ (before deal)</Label>
              <Input type="number" value={form.deal_original_price || ""} onChange={(e) => set("deal_original_price", Number(e.target.value) || 0)} data-testid="prod-deal-original" />
            </div>
            <div>
              <Label>Expires at (YYYY-MM-DDTHH:MM)</Label>
              <Input type="datetime-local" value={(form.deal_expires_at || "").slice(0, 16)} onChange={(e) => set("deal_expires_at", e.target.value ? new Date(e.target.value).toISOString() : "")} data-testid="prod-deal-expires" />
            </div>
            <div>
              <Label>Label (e.g., Early Bird)</Label>
              <Input value={form.deal_label || ""} onChange={(e) => set("deal_label", e.target.value)} placeholder="Flash Sale" data-testid="prod-deal-label" />
            </div>
          </div>
        )}
      </div>

      {/* Booking Cutoff & Capacity */}
      <div className="border border-border rounded-xl p-4 space-y-3">
        <h3 className="font-semibold flex items-center gap-2 text-sm"><Clock className="w-4 h-4 text-primary" /> Capacity & Cutoff</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Total Seats per Date <span className="text-xs text-muted-foreground">(0 = unlimited)</span></Label>
            <Input type="number" min={0} value={form.seats_total || 0} onChange={(e) => set("seats_total", Number(e.target.value) || 0)} data-testid="prod-seats-total" />
          </div>
          <div>
            <Label>Booking Cutoff (hours before travel) <span className="text-xs text-muted-foreground">(0 = no cutoff)</span></Label>
            <Input type="number" min={0} value={form.booking_cutoff_hours || 0} onChange={(e) => set("booking_cutoff_hours", Number(e.target.value) || 0)} data-testid="prod-cutoff-hours" />
          </div>
        </div>
      </div>

      {/* Product Validity */}
      <div className="border border-border rounded-xl p-4 space-y-3">
        <h3 className="font-semibold flex items-center gap-2 text-sm"><CalendarDays className="w-4 h-4 text-primary" /> Product Validity</h3>
        <p className="text-xs text-muted-foreground">Set the date range when this product can be booked. Leave empty for no restriction.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Valid From</Label>
            <Input type="date" value={form.validity_from || ""} onChange={(e) => set("validity_from", e.target.value)} data-testid="prod-validity-from" />
          </div>
          <div>
            <Label>Valid To</Label>
            <Input type="date" value={form.validity_to || ""} onChange={(e) => set("validity_to", e.target.value)} data-testid="prod-validity-to" />
          </div>
        </div>
      </div>

      {/* Date-Based / Surge Pricing */}
      <div className="border border-border rounded-xl p-4 space-y-3">
        <h3 className="font-semibold flex items-center gap-2 text-sm"><TrendingUp className="w-4 h-4 text-primary" /> Date-Based Pricing (Surge / Seasonal)</h3>
        <p className="text-xs text-muted-foreground">Add date-range specific pricing. Prices entered here override base prices for those dates.</p>
        <div className="flex items-center gap-3 mb-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.require_date_pricing || false} onChange={(e) => set("require_date_pricing", e.target.checked)} className="w-4 h-4 rounded" data-testid="prod-require-date-pricing" />
            <span className="text-sm">Block booking if no date pricing exists for selected date</span>
          </label>
        </div>
        <div className="space-y-3">
          {(form.date_based_pricing || []).map((entry, i) => (
            <div key={i} className="border border-dashed border-border rounded-lg p-3 space-y-2 bg-secondary/10">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">{entry.label || `Rate ${i + 1}`}</span>
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => {
                  const arr = [...(form.date_based_pricing || [])];
                  arr.splice(i, 1);
                  set("date_based_pricing", arr);
                }}><X className="w-4 h-4" /></Button>
              </div>
              <div className="grid sm:grid-cols-4 gap-2">
                <div><Label className="text-xs">From Date</Label><Input type="date" value={entry.from_date || ""} onChange={(e) => { const arr = [...(form.date_based_pricing || [])]; arr[i] = { ...arr[i], from_date: e.target.value }; set("date_based_pricing", arr); }} /></div>
                <div><Label className="text-xs">To Date</Label><Input type="date" value={entry.to_date || ""} onChange={(e) => { const arr = [...(form.date_based_pricing || [])]; arr[i] = { ...arr[i], to_date: e.target.value }; set("date_based_pricing", arr); }} /></div>
                <div><Label className="text-xs">Adult ₹</Label><Input type="number" value={entry.adult_price ?? ""} onChange={(e) => { const arr = [...(form.date_based_pricing || [])]; arr[i] = { ...arr[i], adult_price: e.target.value ? Number(e.target.value) : null }; set("date_based_pricing", arr); }} placeholder="Base" /></div>
                <div><Label className="text-xs">Child ₹</Label><Input type="number" value={entry.child_price ?? ""} onChange={(e) => { const arr = [...(form.date_based_pricing || [])]; arr[i] = { ...arr[i], child_price: e.target.value ? Number(e.target.value) : null }; set("date_based_pricing", arr); }} placeholder="Base" /></div>
              </div>
              <div><Label className="text-xs">Label (e.g. Weekend, Peak Season, Holiday)</Label><Input value={entry.label || ""} onChange={(e) => { const arr = [...(form.date_based_pricing || [])]; arr[i] = { ...arr[i], label: e.target.value }; set("date_based_pricing", arr); }} placeholder="Weekend Rate" /></div>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => set("date_based_pricing", [...(form.date_based_pricing || []), { from_date: "", to_date: "", adult_price: null, child_price: null, label: "" }])} data-testid="add-date-pricing">
            <Plus className="w-4 h-4 mr-1" />Add Date Range
          </Button>
        </div>
      </div>

      {/* Online Booking Toggle */}
      <div className="border border-border rounded-xl p-4 bg-secondary/30 space-y-4">
        <div className="flex items-center gap-3">
          <Switch checked={form.online_booking_enabled === true} onCheckedChange={(v) => set("online_booking_enabled", v || undefined)} id="online_booking" data-testid="prod-online-booking" />
          <Label htmlFor="online_booking" className="cursor-pointer font-semibold flex items-center gap-2">
            Enable Online Booking for this product
          </Label>
          <span className="text-xs text-muted-foreground">(overrides global setting)</span>
        </div>
      </div>

      <div className="border border-border rounded-xl p-4 bg-secondary/30 space-y-4">
        <div className="flex items-center gap-3">
          <Switch checked={!!form.advance_payment_enabled} onCheckedChange={(v) => set("advance_payment_enabled", v)} id="advance_pay" data-testid="prod-advance-pay" />
          <Label htmlFor="advance_pay" className="cursor-pointer font-semibold flex items-center gap-2">
            <CreditCard className="w-4 h-4" /> Part Payment / Advance Booking
          </Label>
        </div>
        {form.advance_payment_enabled && (
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Advance % of total</Label>
              <Input type="number" min={10} max={100} value={form.advance_payment_percent || 50} onChange={(e) => set("advance_payment_percent", Number(e.target.value) || 50)} data-testid="prod-advance-percent" />
            </div>
            <div>
              <Label>Or fixed amount ₹ (0 = use %)</Label>
              <Input type="number" min={0} value={form.advance_payment_fixed || 0} onChange={(e) => set("advance_payment_fixed", Number(e.target.value) || 0)} data-testid="prod-advance-fixed" />
            </div>
          </div>
        )}
      </div>

      {/* Pickup & Drop Points */}
      {form.id && (
        <PickupPointsSection productId={form.id} />
      )}
      {!form.id && (
        <div className="border border-border rounded-xl p-4 bg-secondary/20">
          <p className="text-sm text-muted-foreground">Save the product first to add pickup/drop points.</p>
        </div>
      )}

      {/* SEO */}
      <div className="border border-border rounded-xl p-4 space-y-2">
        <h3 className="font-semibold flex items-center gap-2 text-sm">
          <Globe className="w-4 h-4 text-primary" /> SEO Settings
        </h3>
        <SEOEditor
          seoTitle={form.seo_title}
          seoDescription={form.seo_description}
          ogImage={form.og_image}
          focusKeyword={form.focus_keyword}
          slug={form.slug ? `product/${form.slug}` : ""}
          onChange={(k, v) => set(k, v)}
        />
      </div>

      <div className="flex justify-end gap-3 sticky bottom-0 bg-white border-t border-border pt-4 mt-6 -mx-6 px-6 -mb-4 pb-4">
        <Button variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button onClick={submit} disabled={saving} className="bg-saffron hover:bg-saffron-dark text-white" data-testid="prod-save">
          {saving ? "Saving..." : isEdit ? "Update" : "Create"}
        </Button>
      </div>
    </div>
  );
}

export default function AdminProducts() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [dbCategories, setDbCategories] = useState([]);

  // Build dynamic category and subcategory lists from DB
  const rootCats = dbCategories.filter((c) => !c.parent_id);
  const CATEGORIES = rootCats.map((c) => c.product_category || c.slug);
  const SUBS = {};
  rootCats.forEach((parent) => {
    const pc = parent.product_category || parent.slug;
    const children = dbCategories.filter((c) => c.parent_id === parent.id);
    if (children.length > 0) {
      SUBS[pc] = children.map((c) => c.name);
    }
  });

  const load = () => {
    setLoading(true);
    api.get("/admin/products/all", { params: { limit: 200 } })
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get("/categories").then((r) => setDbCategories(r.data)).catch(() => {});
  }, []);

  const create = async (data) => {
    try {
      await api.post("/products", data);
      toast.success("Product created");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Create failed");
      throw err;
    }
  };

  const update = async (data) => {
    try {
      await api.put(`/products/${editing.id}`, data);
      toast.success("Product updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
      throw err;
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggleStatus = async (p) => {
    try {
      const { data } = await api.put(`/admin/products/${p.id}/toggle-status`);
      toast.success(`Product ${data.status === "active" ? "activated" : "deactivated"}`);
      setProducts((prev) => prev.map((prod) => prod.id === p.id ? { ...prod, status: data.status } : prod));
    } catch {
      toast.error("Toggle failed");
    }
  };

  const filtered = products.filter((p) => {
    if (filter) {
      // filter is product_category value (e.g. "cab", "tour", "hotel")
      if (p.category !== filter) return false;
    }
    if (search && !p.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-products-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-teal text-white flex items-center justify-center">
            <PackageIcon className="w-6 h-6" />
          </div>
          <div>
            <h1 className="font-display text-3xl">Products / Packages</h1>
            <p className="text-muted-foreground text-sm">{products.length} total · {filtered.length} shown</p>
          </div>
        </div>
        <Sheet open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
          <SheetTrigger asChild>
            <Button onClick={() => setEditing({})} className="bg-saffron hover:bg-saffron-dark text-white" data-testid="add-product-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Product
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-full sm:max-w-4xl overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="font-display">{editing?.id ? "Edit product" : "New product"}</SheetTitle>
              <SheetDescription>Fill all required fields. Image URLs from Unsplash, your CDN, or any HTTPS source.</SheetDescription>
            </SheetHeader>
            {editing !== null && (
              <ProductForm
                initial={editing}
                onSubmit={editing.id ? update : create}
                onCancel={() => setEditing(null)}
                categories={CATEGORIES}
                subs={SUBS}
                dbCategories={dbCategories}
              />
            )}
          </SheetContent>
        </Sheet>
      </div>

      <div className="flex gap-3 mb-4 flex-wrap">
        <Input placeholder="Search by title..." value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-sm" data-testid="prod-search" />
        <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-44" data-testid="prod-filter-category"><SelectValue placeholder="All categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {(() => {
              const seen = new Set();
              return dbCategories.filter((c) => !c.parent_id).filter((c) => {
                const pc = c.product_category || c.slug;
                if (seen.has(pc)) return false;
                seen.add(pc);
                return true;
              }).map((c) => {
                const pc = c.product_category || c.slug;
                return <SelectItem key={pc} value={pc} className="capitalize">{c.name}</SelectItem>;
              });
            })()}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-border rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">Image</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Rating</TableHead>
              <TableHead>Active</TableHead>
              <TableHead>Featured</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-12">Loading...</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-12">No products match your filters.</TableCell></TableRow>
            ) : filtered.map((p) => (
              <TableRow key={p.id} data-testid={`prod-row-${p.slug}`} className={p.status !== "active" ? "opacity-50" : ""}>
                <TableCell>
                  {p.featured_image ? (
                    <img src={p.featured_image} alt="" className="w-12 h-12 rounded-lg object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center"><ImageIcon className="w-4 h-4 text-muted-foreground" /></div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="font-medium">{p.title}</div>
                  <div className="text-xs text-muted-foreground">{p.slug}</div>
                </TableCell>
                <TableCell><Badge variant="outline" className="capitalize">{p.category}{p.sub_category && ` · ${p.sub_category}`}</Badge></TableCell>
                <TableCell className="text-teal font-medium">{formatINR(p.adult_price)}</TableCell>
                <TableCell><span className="flex items-center gap-1 text-sm"><Star className="w-3.5 h-3.5 fill-saffron text-saffron" />{p.rating?.toFixed(1)} ({p.review_count})</span></TableCell>
                <TableCell>
                  <Switch
                    checked={p.status === "active"}
                    onCheckedChange={() => toggleStatus(p)}
                    data-testid={`toggle-${p.slug}`}
                  />
                </TableCell>
                <TableCell>{p.is_featured && <Badge className="bg-saffron text-white">Featured</Badge>}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" onClick={() => navigate(`/admin/itinerary/${p.id}`)} title="Itinerary Builder" data-testid={`itinerary-${p.slug}`}><CalendarDays className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => setEditing(p)} data-testid={`edit-${p.slug}`}><Edit className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(p)} className="text-destructive hover:text-destructive" data-testid={`delete-${p.slug}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
